import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDownIcon, SearchIcon } from 'lucide-react';
import { PageHero } from '../components/layout/PageHero';
import { EmptyState } from '../components/ui/EmptyState';
import { CtaBand } from '../components/ui/CtaBand';
import { faqCategories, faqs } from '../data/faqs';
export function Faq() {
  const [category, setCategory] = useState('All');
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState<string | null>(faqs[0].id);
  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    return faqs.filter(faq => {
      const matchesCategory = category === 'All' || faq.category === category;
      const matchesQuery = !term || faq.question.toLowerCase().includes(term) || faq.answer.toLowerCase().includes(term);
      return matchesCategory && matchesQuery;
    });
  }, [category, query]);
  return <>
      <PageHero eyebrow="Frequently asked questions" title="Answers to the questions we are asked most." intro="If your question is not here, the campaign will answer it directly — and publish the answer if it is useful to others." crumbs={[{
      label: 'Home',
      to: '/'
    }, {
      label: 'FAQ'
    }]} data-id="element-911" data-mp="3lplcm3v0wudr" />

      <section className="bg-sand-50 py-14 lg:py-20" aria-label="Questions and answers" data-id="element-912" data-mp="2k4rbm60sw6it">
        <div className="mx-auto grid max-w-content gap-10 px-4 sm:px-6 lg:grid-cols-12 lg:px-10" data-id="element-913" data-mp="2p3kllogvpfe8">
          <aside className="lg:col-span-4" data-id="element-914" data-mp="2t4o4yky4xtk7">
            <div className="lg:sticky lg:top-28" data-id="element-915" data-mp="2t3cz8fl4qbqg">
              <div className="relative" data-id="element-916" data-mp="2xvweri3dngxw">
                <label htmlFor="faq-search" className="sr-only" data-id="element-917" data-mp="2xwxsjubs2hjb">
                  Search questions
                </label>
                <SearchIcon aria-hidden className="pointer-events-none absolute left-4 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-500" data-id="element-918" data-mp="32wtt1jl9nqu4" />
                <input id="faq-search" type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder="Search questions" className="w-full rounded-lg border border-sand-300 bg-white py-3 pl-11 pr-4 text-[15px] text-ink placeholder:text-slate-500/70 focus:border-green-600 focus:outline-none" data-id="element-919" data-mp="3cqsushhfh8lj" />
              </div>
              <ul className="mt-6 space-y-1" role="group" aria-label="Filter by category" data-id="element-920" data-mp="35ty1voxzfitu">
                {faqCategories.map(item => {
                const active = category === item;
                return <li key={item} data-id="element-921" data-mp="3fsnkxl1m79bo">
                      <button type="button" aria-pressed={active} onClick={() => setCategory(item)} className={`w-full border-l-2 py-2.5 pl-4 text-left text-[15px] transition-colors duration-150 ease-emphasis ${active ? 'border-green-600 bg-white font-semibold text-ink' : 'border-sand-300 text-slate-600 hover:border-green-400 hover:text-ink'}`} data-id="element-922" data-mp="3aulqy52nhplf">
                        {item}
                      </button>
                    </li>;
              })}
              </ul>
            </div>
          </aside>

          <div className="lg:col-span-8" data-id="element-923" data-mp="39blrdcvje2z7">
            {filtered.length === 0 ? <EmptyState title="No questions match that search" description="Try a different word, or contact the campaign and we will answer directly." actionLabel="Clear search" onAction={() => {
            setQuery('');
            setCategory('All');
          }} data-id="element-924" data-mp="3dftg3a1zvixf" /> : <ul className="divide-y divide-sand-300 border-y border-sand-300" data-id="element-925" data-mp="12mz2hnoy8bmp">
                {filtered.map(faq => {
              const expanded = open === faq.id;
              return <li key={faq.id} id={faq.id} className="scroll-mt-28" data-id="element-926" data-mp="2tqiteoh8qbhw">
                      <h2 data-id="element-927" data-mp="2pox9wka7ayyq">
                        <button type="button" onClick={() => setOpen(expanded ? null : faq.id)} aria-expanded={expanded} aria-controls={`${faq.id}-panel`} className="flex w-full items-start justify-between gap-6 py-5 text-left" data-id="element-928" data-mp="2po4ak4dknbqm">
                          <span className="font-display text-[19px] leading-snug text-ink" data-id="element-929" data-mp="260odtz5je8ty">
                            {faq.question}
                          </span>
                          <ChevronDownIcon aria-hidden className={`mt-1 h-5 w-5 shrink-0 text-green-600 transition-transform duration-200 ease-emphasis ${expanded ? 'rotate-180' : ''}`} data-id="element-930" data-mp="21xrwebrqgirl" />
                        </button>
                      </h2>
                      <AnimatePresence initial={false} data-id="element-931" data-mp="ckk3fa14wfby">
                        {expanded && <motion.div id={`${faq.id}-panel`} initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.22,
                    ease: [0.23, 1, 0.32, 1]
                  }} className="overflow-hidden" data-id="element-932" data-mp="hffuo7yjqyo0">
                            <p className="max-w-2xl pb-6 text-[16px] leading-relaxed text-slate-600" data-id="element-933" data-mp="2lbdqtdaxmve">
                              {faq.answer}
                            </p>
                          </motion.div>}
                      </AnimatePresence>
                    </li>;
            })}
              </ul>}
          </div>
        </div>
      </section>

      <CtaBand title="Still have a question?" description="The campaign answers every message, and publishes the answers that help others." primary={{
      label: 'Contact the campaign',
      to: '/contact'
    }} secondary={{
      label: 'Facts & answers',
      to: '/facts'
    }} data-id="element-934" data-mp="8f09jubjsk9j" />
    </>;
}